import { ComponentIds, HierarchyUtils, MeasurementUtils } from '@inter-ikea-core/core';
import { EntityApi, OuterMeasurementsComponent, ProductType, transformOuterMeasurements } from '@inter-ikea-storageone/range';
import * as R from 'ramda';
export class BillyEntityApi extends EntityApi {
    constructor() {
        super(...arguments);
        this.isCornerFrame = (frame) => {
            const frameAssembly = HierarchyUtils.parent(frame);
            const guestConnection = frameAssembly?.getComponent(ComponentIds.GUEST_CONNECTIONS_COMPONENT_ID)?.highestPriorityConnection;
            return guestConnection?.guestConnector.key === 'wall-corner';
        };
        this.adjustForCornerFrame = (wallEntities, wall, measurements) => {
            const frameDepth = wallEntities[0]?.getComponent(ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID)?.size.depth;
            const adjustmentForCornerFrame = frameDepth === 400 ? 673 : 563;
            let adjustedMeasurements = measurements;
            wallEntities.forEach(entity => {
                const alignedFrames = this.getAlignedFrames(entity, 'align-left', 'align-right', true, true);
                if (alignedFrames.left && this.isCornerFrame(alignedFrames.left)) {
                    if (wall === 'front') {
                        adjustedMeasurements = {
                            ...adjustedMeasurements,
                            width: adjustedMeasurements.width + adjustmentForCornerFrame,
                            min: {
                                ...adjustedMeasurements.min,
                                x: adjustedMeasurements.min.x - adjustmentForCornerFrame
                            }
                        };
                    }
                    else if (wall === 'right') {
                        adjustedMeasurements = {
                            ...adjustedMeasurements,
                            width: adjustedMeasurements.width + adjustmentForCornerFrame,
                            min: {
                                ...adjustedMeasurements.min,
                                z: adjustedMeasurements.min.z - adjustmentForCornerFrame
                            }
                        };
                    }
                    else if (wall === 'left') {
                        adjustedMeasurements = {
                            ...adjustedMeasurements,
                            width: adjustedMeasurements.width + adjustmentForCornerFrame,
                            max: {
                                ...adjustedMeasurements.max,
                                z: adjustedMeasurements.max.z + adjustmentForCornerFrame
                            }
                        };
                    }
                    else if (wall === 'back') {
                        adjustedMeasurements = {
                            ...adjustedMeasurements,
                            width: adjustedMeasurements.width + adjustmentForCornerFrame,
                            max: {
                                ...adjustedMeasurements.max,
                                x: adjustedMeasurements.max.x + adjustmentForCornerFrame
                            }
                        };
                    }
                }
                if (alignedFrames.right && this.isCornerFrame(alignedFrames.right)) {
                    if (wall === 'front') {
                        adjustedMeasurements = {
                            ...adjustedMeasurements,
                            width: adjustedMeasurements.width + adjustmentForCornerFrame,
                            max: {
                                ...adjustedMeasurements.max,
                                x: adjustedMeasurements.max.x + adjustmentForCornerFrame
                            }
                        };
                    }
                    else if (wall === 'right') {
                        adjustedMeasurements = {
                            ...adjustedMeasurements,
                            width: adjustedMeasurements.width + adjustmentForCornerFrame,
                            max: {
                                ...adjustedMeasurements.max,
                                z: adjustedMeasurements.max.z + adjustmentForCornerFrame
                            }
                        };
                    }
                    else if (wall === 'left') {
                        adjustedMeasurements = {
                            ...adjustedMeasurements,
                            width: adjustedMeasurements.width + adjustmentForCornerFrame,
                            min: {
                                ...adjustedMeasurements.min,
                                z: adjustedMeasurements.min.z - adjustmentForCornerFrame
                            }
                        };
                    }
                    else if (wall === 'back') {
                        adjustedMeasurements = {
                            ...adjustedMeasurements,
                            width: adjustedMeasurements.width + adjustmentForCornerFrame,
                            min: {
                                ...adjustedMeasurements.min,
                                x: adjustedMeasurements.min.x - adjustmentForCornerFrame
                            }
                        };
                    }
                }
            });
            return adjustedMeasurements;
        };
    }
    /**
     * @override Custom duplicate to make sure we display toast messages when duplicating extension fails
     */
    duplicate() {
        const selectedEntity = this.findSelectedEntity();
        if (!selectedEntity) {
            this.reportErrorToSentry(new Error('Found no entity that is selected'));
            return null;
        }
        const copyEntity = HierarchyUtils.duplicateEntity(selectedEntity, this.core.services);
        const placementResult = this.placementService.snapToScene(copyEntity);
        if (!placementResult.valid) {
            this.callbacks.onInvalidDuplicationPlacement?.({ selectedEntity, copyEntity });
        }
        return placementResult;
    }
    /**
     * @override
     */
    getOuterMeasurementEntitiesPerWall() {
        const entities = this.getByComponent(OuterMeasurementsComponent.id);
        if (entities.length === 0) {
            return null;
        }
        return entities.reduce((acc, entity) => {
            const wall = this.getWallForEntity(entity);
            return wall
                ? {
                    ...acc,
                    [wall]: R.uniqBy(R.prop('id'), [...acc[wall], entity])
                }
                : acc;
        }, { front: [], left: [], right: [], back: [] });
    }
    /**
     * @override handle BILLY corner frames
     */
    getOuterMeasurementsPerWall() {
        const groupedEntities = this.getOuterMeasurementEntitiesPerWall();
        if (!groupedEntities) {
            return null;
        }
        return Object.entries(groupedEntities).reduce((acc, [wall, entities]) => {
            const outsideMeasurements = MeasurementUtils.getOutsideMeasurementWithoutHierarchies(entities);
            if (!outsideMeasurements) {
                return acc;
            }
            const transformedMeasurements = transformOuterMeasurements(outsideMeasurements, wall === 'left' || wall === 'right');
            if (transformedMeasurements === null) {
                return acc;
            }
            const measurements = this.adjustForCornerFrame(entities, wall, transformedMeasurements);
            return {
                ...acc,
                [wall]: measurements
            };
        }, {});
    }
    /**
     * @override
     */
    getEntityWall(entity) {
        const rotation = entity
            .getComponent(ComponentIds.WORLD_TRANSFORM_COMPONENT_ID)
            ?.transform?.rotation?.toEulerDegrees();
        if (rotation && (Math.round(rotation.Y) === 180 || Math.round(rotation.Y) === -180)) {
            return 'back';
        }
        if (rotation && (Math.round(rotation.Y) === 90 || Math.round(rotation.Y) === 135)) {
            return 'left';
        }
        else if (rotation && (Math.round(rotation.Y) === -90 || Math.round(rotation.Y) === -135)) {
            return 'right';
        }
        return 'front';
    }
    /**
     * @override
     */
    getAlignedFrames(frame, leftAlignmentConnectorKey, rightAlignmentConnectorKey, matchHeigth, matchDepth) {
        const isSameDepth = (frame1, frame2) => {
            return frame1.id !== frame2.id
                ? this.getEntityComSize(frame1)?.depth === this.getEntityComSize(frame2)?.depth
                : false;
        };
        const isSameHeight = (frame1, frame2) => {
            return frame1.id !== frame2.id
                ? this.getEntityComSize(frame1)?.height === this.getEntityComSize(frame2)?.height
                : false;
        };
        const leftAlignedFrame = this.getByProductType(ProductType.BILLY_FRAME)
            .filter(f => (matchHeigth ? isSameHeight(frame, f) : true))
            .filter(f => (matchDepth ? isSameDepth(frame, f) : true))
            .find(f => this.isEntityConnectorsAligned(frame, leftAlignmentConnectorKey, f, rightAlignmentConnectorKey)) || null;
        const rightAlignedFrame = this.getByProductType(ProductType.BILLY_FRAME)
            .filter(f => (matchHeigth ? isSameHeight(frame, f) : true))
            .filter(f => (matchDepth ? isSameDepth(frame, f) : true))
            .find(f => this.isEntityConnectorsAligned(f, leftAlignmentConnectorKey, frame, rightAlignmentConnectorKey)) || null;
        return { left: leftAlignedFrame, right: rightAlignedFrame };
    }
    getWallForEntity(entity) {
        const rotation = entity
            .getComponent(ComponentIds.WORLD_TRANSFORM_COMPONENT_ID)
            ?.transform?.rotation?.toEulerDegrees();
        if (!rotation)
            return null;
        if (Math.round(rotation.Y) === 0) {
            return 'front';
        }
        else if (Math.round(rotation.Y) === 180 || Math.round(rotation.Y) === -180) {
            return 'back';
        }
        else if (Math.round(rotation.Y) === 90) {
            return 'left';
        }
        else if (Math.round(rotation.Y) === -90) {
            return 'right';
        }
        return null;
    }
}
//# sourceMappingURL=BillyEntityApi.js.map